import { getDisplayPrice } from '../assets/addAssetPrice.ts'
import {
  assetConditionSchema,
  type AssetCondition,
  type BuildingType,
} from '../assets/assetDataTypes.ts'
import { type ParsedAsset } from '../assets/assetTypes.ts'
import type { PropertyFiltersCoreType, PropertyFiltersType } from './filterTypes.ts'

function roundToNearest(value: number, step: number) {
  return Math.round(value / step) * step
}

function getPrimaryUnit(asset: ParsedAsset) {
  return asset.data?.units?.[0]
}

const similarMap: Record<BuildingType, BuildingType[]> = {
  apartment: ['apartment', 'row', 'duplex'],
  row: ['row', 'duplex', 'apartment', 'detached'],
  duplex: ['duplex', 'row', 'detached'],
  detached: ['detached', 'duplex'],
  farmhouse: ['farmhouse', 'detached'],
  other: ['other'],
}

function deriveBuildingTypes(assetType: BuildingType | undefined): BuildingType[] | undefined {
  if (!assetType) return undefined
  return similarMap[assetType]
}

function deriveConditions(
  assetCondition: AssetCondition | undefined
): AssetCondition[] | undefined {
  const order = assetConditionSchema.options.map((c) => c.value).slice(0, 4)
  const defaultFiltering = order.slice(0, 3)

  if (!assetCondition) return defaultFiltering

  const index = order.indexOf(assetCondition)
  if (index === -1) return defaultFiltering

  const guardMax = (value: number) => Math.min(value, order.length - 1)
  return order.slice(0, guardMax(index + 1))
}

export function deriveFiltersFromFeedback({
  asset,
  editor,
}: {
  asset: ParsedAsset | null
  editor: PropertyFiltersCoreType
}): Partial<PropertyFiltersType> {
  if (!asset) {
    return {}
  }

  const unit = getPrimaryUnit(asset)
  const assetRooms = unit?.rooms ?? undefined
  const assetSize = unit?.size ?? undefined
  const assetBuildYear = unit?.buildYear ?? undefined
  const assetType = asset.data?.propertyType ?? undefined
  const assetCondition = unit?.condition ?? undefined

  const askingPrice = getDisplayPrice(asset.prices || [])?.price

  let priceMax: number | undefined = undefined
  let priceMin: number | undefined = undefined

  if (typeof askingPrice === 'number') {
    priceMax = roundToNearest(askingPrice * 1.1, 10000)
    priceMin = roundToNearest(askingPrice * 0.75, 10000)
  }

  let roomsMin: number | undefined = undefined

  if (typeof assetRooms === 'number') {
    roomsMin = assetRooms
  }

  let sizeMin: number | undefined = undefined

  if (typeof assetSize === 'number') {
    sizeMin = roundToNearest(assetSize * 0.95, 10)
  }

  let buildYearMin: string | undefined = undefined

  if (assetBuildYear) {
    buildYearMin = String(assetBuildYear)
  }

  if (editor === 'price') {
    const result: Partial<PropertyFiltersType> = {}

    if (typeof priceMax === 'number') {
      result.priceMax = priceMax
    }

    if (typeof priceMin === 'number') {
      result.priceMin = priceMin
    }

    return result
  }

  if (editor === 'rooms') {
    const result: Partial<PropertyFiltersType> = {}

    if (typeof roomsMin === 'number') {
      result.roomsMin = roomsMin
    }

    return result
  }

  if (editor === 'size') {
    const result: Partial<PropertyFiltersType> = {}

    if (typeof sizeMin === 'number') {
      result.sizeMin = sizeMin
    }

    return result
  }

  if (editor === 'year') {
    const result: Partial<PropertyFiltersType> = {}

    if (typeof buildYearMin === 'string') {
      result.buildYearMin = buildYearMin
    }

    return result
  }

  if (editor === 'buildingType') {
    const result: Partial<PropertyFiltersType> = {}
    const bt = deriveBuildingTypes(assetType)

    if (bt && bt.length > 0) {
      result.buildingTypes = bt
    }

    return result
  }

  if (editor === 'condition') {
    const result: Partial<PropertyFiltersType> = {}
    const cond = deriveConditions(assetCondition)

    if (cond && cond.length > 0) {
      result.conditions = cond
    }

    return result
  }

  return {}
}
